import { buttonVariants } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@arcadeai/design-system/components/ui/atoms/table";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Filter, Settings } from "lucide-react";

export const ToolsWidgetSkeleton = () => {
  return (
    <div className="grid grid-cols-1">
      <Card>
        <CardHeader className="mt-1 flex flex-row justify-between pb-2">
          <div className="flex flex-col gap-2">
            <CardTitle>Tools</CardTitle>
            <CardDescription>
              <Skeleton className="h-4 w-16" />
            </CardDescription>
          </div>
          <Link
            className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}
            from="/orgs/$organizationId/projects/$projectId/tools"
            to="."
          >
            View all
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </CardHeader>
        <CardContent className="my-2 space-y-4">
          {/* Search and Filters Row */}
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex flex-wrap items-center gap-2">
              {/* Search Input Skeleton */}
              <Skeleton className="h-9 w-64" />

              {/* Filter Buttons Skeleton */}
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-8 w-16" />
              </div>
            </div>

            {/* View Button Skeleton */}
            <div className="flex items-center space-x-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <Skeleton className="h-8 w-12" />
            </div>
          </div>

          {/* Table Container with proper shadcn Table structure */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Skeleton className="h-4 w-12" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-20" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-14" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-14" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-24" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-16" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <ToolRowSkeleton />
                <ToolRowSkeleton />
                <ToolRowSkeleton />
                <ToolRowSkeleton />
                <ToolRowSkeleton />
              </TableBody>
            </Table>
          </div>

          {/* Pagination Skeleton */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-muted-foreground text-sm">
                Rows per page
              </span>
              <Skeleton className="h-8 w-16" />
            </div>
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-24" /> {/* Page info */}
              <div className="flex space-x-1">
                <Skeleton className="h-8 w-8" /> {/* First page */}
                <Skeleton className="h-8 w-8" /> {/* Previous */}
                <Skeleton className="h-8 w-8" /> {/* Next */}
                <Skeleton className="h-8 w-8" /> {/* Last page */}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ToolRowSkeleton = () => {
  return (
    <TableRow>
      {/* Name */}
      <TableCell>
        <Skeleton className="h-4 w-24" />
      </TableCell>

      {/* Description */}
      <TableCell>
        <Skeleton className="h-4 w-full max-w-64" />
      </TableCell>

      {/* Toolkit */}
      <TableCell>
        <div className="flex items-center space-x-2">
          <Skeleton className="h-5 w-5 rounded-full" />
          <Skeleton className="h-4 w-16" />
        </div>
      </TableCell>

      {/* Version */}
      <TableCell>
        <Skeleton className="h-4 w-8" />
      </TableCell>

      {/* Requirements */}
      <TableCell>
        <div className="flex items-center space-x-1">
          <Skeleton className="h-5 w-5 rounded-full" />
          <Skeleton className="h-4 w-12" />
        </div>
      </TableCell>

      {/* Actions */}
      <TableCell>
        <div className="flex items-center space-x-2">
          <Skeleton className="h-8 w-12" />
          <Skeleton className="h-8 w-16" />
        </div>
      </TableCell>
    </TableRow>
  );
};
